import { ShieldCheck, ShieldAlert, MoonStar } from "lucide-react";

/**
 * Professional SVG progress ring for System Uptime %.
 * Green tone above 90 %, amber 70–90 %, red below 70 %.
 * If `uptime.uptime_pct === null` the plant is outside daylight hours;
 * we render a soft "off-hours" state instead of a fake 0 %.
 */
export default function UptimeRing({ uptime, size = 92, label = "System Availability", testid = "uptime-ring" }) {
    const pct = uptime?.uptime_pct;
    const inDaylight = uptime?.in_daylight;

    // Sensible thresholds — inverters that were up >90% of daylight are excellent
    let color = "#16A34A";
    let ring = "GOOD";
    let Icon = ShieldCheck;
    if (pct === null || pct === undefined) {
        color = "#71717A";
        ring = "OFF-HOURS";
        Icon = MoonStar;
    } else if (pct < 70) {
        color = "#DC2626";
        ring = "LOW";
        Icon = ShieldAlert;
    } else if (pct < 90) {
        color = "#F59E0B";
        ring = "OK";
        Icon = ShieldAlert;
    }

    const stroke = 8;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pctForRing = pct == null ? 0 : Math.max(0, Math.min(100, pct));
    const offset = c - (pctForRing / 100) * c;

    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-4 flex items-center gap-4" data-testid={testid}>
            <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        stroke="#E4E4E7"
                        strokeWidth={stroke}
                        fill="none"
                    />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        stroke={color}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={c}
                        strokeDashoffset={offset}
                        fill="none"
                        style={{ transition: "stroke-dashoffset 700ms cubic-bezier(0.16, 1, 0.3, 1)" }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {pct == null ? (
                        <>
                            <Icon className="w-4 h-4" style={{ color }} />
                            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Night</div>
                        </>
                    ) : (
                        <>
                            <div className="text-xl font-medium font-mono" style={{ color }} data-testid={`${testid}-value`}>
                                {pct.toFixed(1)}
                            </div>
                            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 leading-none">%</div>
                        </>
                    )}
                </div>
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                    <Icon className="w-3 h-3" style={{ color }} /> {label}
                </div>
                <div className="text-sm text-zinc-800 mt-0.5 font-medium">
                    {pct == null ? (
                        "Outside daylight hours"
                    ) : (
                        <span data-testid={`${testid}-status`}>{ring} · Uptime today</span>
                    )}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                    {inDaylight && uptime?.up_minutes != null && uptime?.denominator_minutes != null && (
                        <>producing {uptime.up_minutes} / {uptime.denominator_minutes} min · 06:00–18:00 IST</>
                    )}
                    {!inDaylight && "Solar hours 06:00–18:00 IST"}
                </div>
            </div>
        </div>
    );
}
